import simpleRestProvider from 'ra-data-simple-rest';
import { DataProvider, GetOneParams, GetOneResult, Identifier, RaRecord, fetchUtils } from 'react-admin';
import { getLocalStorageId } from './authProvider';
import fileDownload from 'js-file-download';

const API_URL = import.meta.env.VITE_SIMPLE_REST_URL;
const REPLACE_ID = '{clientId}';

const getRequestOptions = (method: string, body?: any) => {
  const token = localStorage.getItem('token');
  const options: RequestInit = {
    method,
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    })
  };
  if (body) {
    options.body = JSON.stringify(body);
  }
  return options;
};

const mergeHeaders = (defaultHeaders: Headers, customHeaders: Headers): Headers => {
  const mergedHeaders = new Headers();

  // デフォルトのヘッダーを追加
  defaultHeaders.forEach((value, key) => {
    mergedHeaders.append(key, value);
  });

  // カスタムのヘッダーを追加し、デフォルトのヘッダーを上書き
  if (customHeaders) {
    customHeaders.forEach((value, key) => {
      mergedHeaders.set(key, value);
    });
  }

  return mergedHeaders;
};

const httpClient = (url: any, options: any = {}) => {
  const requestUrl = url.replace(REPLACE_ID, getLocalStorageId() ?? '');
  const pathname = new URL(requestUrl).pathname;
  if (pathname.startsWith('/register')) {
    return fetchUtils.fetchJson(requestUrl, options);
  } else {
    const defaultOptions = getRequestOptions('GET');
    const requestOptions = {
      ...defaultOptions,
      ...options,
      headers: mergeHeaders(defaultOptions.headers as Headers, options.headers as Headers)
    };
    return fetchUtils.fetchJson(requestUrl, requestOptions);
  }
};

export const getMeData = async () => {
  const response = await fetch(import.meta.env.VITE_SIMPLE_REST_URL + '/me', {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }
  });
  if (!response.ok) {
    throw {
      code: 'MeEndpointException'
    };
  }
  return await response.json();
};

/**
 * Convert a `File` object returned by the upload input into a base 64 string.
 * That's not the most optimized way to store images in production, but it's
 * enough to illustrate the idea of dataprovider decoration.
 */
const convertFileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = (error) => reject(error);
  });
};

const convertFilesInObjectToBase64 = async (obj: any): Promise<any> => {
  if (obj !== null && typeof obj === 'object') {
    const entries = Object.entries(obj);
    const result: any = {};
    for (const [key, value] of entries) {
      if (value && typeof value === 'object') {
        if ('rawFile' in value && value.rawFile instanceof File) {
          const base64 = await convertFileToBase64(value.rawFile);
          result[key] = base64;
        } else if ('src' in value && typeof value.src === 'string') {
          const parts = value.src.split(',');
          if (parts.length > 1 && parts[0].includes('base64')) {
            // 'data:image/...;base64,' プレフィックスがある場合
            result[key] = parts[1]; // Base64 部分だけをセット
          } else {
            // プレフィックスがないか、URL (http://...) などの場合
            result[key] = value.src; // そのままセット (バリデーションエラーになるかもしれないが、それは期待通りの動作)
          }
        } else {
          // その他のオブジェクト（Date、配列など）をそのまま保持
          result[key] = value;
        }
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  return obj;
};

const uploadFile = async (data: File, urlSub: string) => {
  const clientId = getLocalStorageId();
  const url = `${API_URL}/${clientId}/${urlSub}`;

  const body = new FormData();
  body.append('file', data);

  const token = localStorage.getItem('token');
  const options: RequestInit = {
    method: 'POST',
    headers: new Headers({
      Authorization: `Bearer ${token}`
    }),
    body: body
  };

  const response = await fetch(url, options);
  if (!response.ok) {
    let bodyError: any = {};
    try {
      const error = await response.json();
      bodyError = { errors: { file: error.message } };
    } catch (error) {
      throw new Error('Network response was not ok');
    }
    throw bodyError;
  }
  return await response.json();
};

const baseDataProvider = simpleRestProvider(API_URL + '/' + REPLACE_ID, httpClient);

export const dataProvider: DataProvider = {
  ...baseDataProvider,

  getMany: async (resource, params) => {
    if (resource === 'affiliates') {
      const clientId = getLocalStorageId();
      const limit = params.ids.length > 0 ? params.ids.length - 1 : 0;
      const url = `${API_URL}/${clientId}/affiliates?filter=${encodeURIComponent(
        JSON.stringify({ id: params.ids })
      )}&range=${encodeURIComponent(JSON.stringify([0, limit]))}`;
      const { json } = await httpClient(url);
      return { data: json };
    }
    return baseDataProvider.getMany(resource, params);
  },

  update: async (resource, params) => {
    if (resource === 'client-pr') {
      const clientId = getLocalStorageId();
      const url = `${API_URL}/${clientId}/client-pr`;
      const options = getRequestOptions('PUT', params.data);
      return httpClient(url, options).then(({ json }) => ({ data: { ...json, id: params.id } }));
    }
    const dataWithBase64 = await convertFilesInObjectToBase64(params.data);
    return baseDataProvider.update(resource, {
      ...params,
      data: dataWithBase64
    });
  },

  getList: (resource, params) => {
    // ▼▼▼ client-pr-images の特別処理を追加 ▼▼▼
    if (resource === 'client-pr-images') {
      const clientId = getLocalStorageId();
      // バックエンド (GET /clients/:clientId/client-pr-images) を叩く
      const url = `${API_URL}/${clientId}/client-pr-images`;

      // httpClient を直接呼び出す
      return httpClient(url, getRequestOptions('GET')).then(({ json }) => {
        // バックエンドは画像の配列 [...] を直接返す
        // react-admin が期待する { data: [...], total: ... } 形式に変換
        return {
          data: json.map((item: any) => ({ ...item, id: item.id })),
          total: json.length
        };
      });
    }
    return baseDataProvider.getList(resource, params);
  },

  getData: async (resource: string) => {
    const clientId = getLocalStorageId();
    const url = `${API_URL}/${clientId}/${resource}`;
    const response = await fetchUtils.fetchJson(url, getRequestOptions('GET'));
    return response.json;
  },

  getBlob: async (resource: string): Promise<Blob> => {
    const clientId = getLocalStorageId();
    const url = `${API_URL}/${clientId}/${resource}`;

    const token = localStorage.getItem('token') ?? undefined;
    const response = await fetch(url, {
      method: 'GET',
      headers: new Headers({
        Accept: 'application/octet-stream',
        Authorization: `Bearer ${token}`
      })
    });
    return await response.blob();
  },

  getOne: async <RecordType extends RaRecord<Identifier>>(
    resource: string,
    params: GetOneParams<RecordType>
  ): Promise<GetOneResult<RecordType>> => {
    if (resource === 'client-pr') {
      const clientId = getLocalStorageId();
      const url = `${API_URL}/${clientId}/client-pr`;
      return httpClient(url, getRequestOptions('GET')).then(({ json }) => ({
        data: { ...json, id: params.id }
      }));
    }
    // 上記以外は、既存のgetOneのロジックを実行
    const requestUrl = `${API_URL}/${REPLACE_ID}/${resource}/${params.id}`;
    return httpClient(requestUrl).then(({ json }) => ({
      data: json
    }));
  },

  create: async (resource, params) => {
    if (resource === 'client-pr-images') {
      const clientId = getLocalStorageId();
      const url = `${API_URL}/${clientId}/client-pr-images`;

      if (!params.data.availableImages) {
        return Promise.resolve({ data: { id: null } });
      }

      const filesToUpload = Array.isArray(params.data.availableImages)
        ? params.data.availableImages
        : [params.data.availableImages];

      try {
        const MAX_SIZE_MB = 2;
        const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
        const ALLOWED_TYPES = ['image/jpeg', 'image/png'];
        const ALLOWED_EXTENSIONS = ['.jpeg', '.jpg', '.png'];

        // 1. アップロードする全ファイルをまず検証する
        for (const file of filesToUpload) {
          if (file && file.rawFile) {
            const rawFile: File = file.rawFile;
            const fileName = rawFile.name || file.title || '';
            // 1a. 拡張子チェック (HEIC対策)
            const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
            if (fileExtension === '' || !ALLOWED_EXTENSIONS.includes(fileExtension)) {
              throw new Error(
                `ファイル ${fileName} (${fileExtension || '不明な'} 形式) は JPEG または PNG 形式である必要があります。`
              );
            }

            // 1b. 形式チェック (MIMEタイプ)
            if (!ALLOWED_TYPES.includes(rawFile.type)) {
              throw new Error(`ファイル ${fileName} (${rawFile.type}) は JPEG または PNG 形式である必要があります。`);
            }

            // 1c. 容量チェック
            if (rawFile.size > MAX_SIZE_BYTES) {
              throw new Error(
                `ファイル ${fileName} は ${MAX_SIZE_MB}MB を超えています (${(rawFile.size / 1024 / 1024).toFixed(1)}MB)`
              );
            }
          }
        }
        // ▲▲▲ バリデーション完了 ▲▲▲

        // 2. 検証をパスしたファイルのみをアップロード処理にかける
        const uploadPromises = filesToUpload.map(async (file) => {
          if (file && file.rawFile) {
            const base64 = await convertFileToBase64(file.rawFile);
            const payload = {
              availableImages: base64,
              filename: file.rawFile.name // 元のファイル名を送信
            };
            return httpClient(url, getRequestOptions('POST', payload));
          }
          return Promise.resolve(null);
        });

        await Promise.all(uploadPromises);

        return { data: { id: null, ...filesToUpload[filesToUpload.length - 1] } };
      } catch (error) {
        let errorMessage = '画像のアップロード中に不明なエラーが発生しました。';
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        throw new Error(errorMessage);
      }
    }

    return baseDataProvider.create(resource, params);
  },
  delete: (resource, params) => {
    // client-pr-images の特別処理を追加
    if (resource === 'client-pr-images') {
      const clientId = getLocalStorageId();
      // バックエンド (DELETE /clients/:clientId/client-pr-images/:id) を叩く
      // (simpleRestProvider のデフォルトの delete 動作がこれに合致する)
      // baseDataProvider が /clients/{clientId}/... を指しているので、
      // デフォルトの delete を呼び出すだけでOK
      return baseDataProvider.delete(resource, params);
    }

    return baseDataProvider.delete(resource, params);
  },

  createAccounts: async (data: { name: string; email: string; initialPassword: string }) => {
    const clientId = getLocalStorageId();
    const url = `${API_URL}/${clientId}/accounts`;
    const response = await fetch(url, getRequestOptions('POST', data));
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return await response.json();
  },

  bulkInvitations: async (file: File, isCompany?: boolean) => {
    const clientId = getLocalStorageId();
    const url = `${API_URL}/${clientId}/bulkInvitations`;

    const body = new FormData();
    body.append('file', file);
    if (isCompany !== undefined) {
      body.append('isCompany', String(isCompany));
    }

    const token = localStorage.getItem('token');
    const options: RequestInit = {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${token}`
      }),
      body: body
    };

    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return await response.json();
  },

  bulkUpdateOrders: async (endpoint: string, orderIds: string[]) => {
    const url = `${API_URL}/${REPLACE_ID}/${endpoint}`;
    const body = { orderIds };
    const options = getRequestOptions('POST', body);
    const { json } = await httpClient(url, options);
    return json;
  },

  updateEcCredentials: (params: { clientId: string; data: any }) => {
    const { clientId, data } = params;
    const url = `${API_URL}/${clientId}/api-settings`;
    const options = getRequestOptions('PUT', data);
    return fetchUtils.fetchJson(url, options).then(({ json }) => ({
      data: json
    }));
  },

  getAuthUrl: (params: { clientId: string }) => {
    const { clientId } = params;
    const url = `${API_URL}/${clientId}/api-settings/auth-url`;
    return fetchUtils.fetchJson(url, getRequestOptions('GET')).then(({ json }) => ({
      data: json
    }));
  },

  updateAutoApprovalFlag: (clientId: number, data: { autoOrderApprovalFlag: number }) => {
    const url = `${API_URL}/${REPLACE_ID}/settings/auto-order-approval-flag`;
    const options = getRequestOptions('PATCH', data);
    return httpClient(url, options).then(({ json }) => ({ data: json }));
  },

  getReportData: async (
    startDate: string,
    endDate: string,
    dateType: number,
    resultType: number,
    couponFlag: number
  ) => {
    try {
      const clientId = getLocalStorageId();
      const url = `${API_URL}/${clientId}/reports?startDate=${startDate}&endDate=${endDate}&dateType=${dateType}&resultType=${resultType}&couponFlag=${couponFlag}`;
      const response = await fetchUtils.fetchJson(url, getRequestOptions('GET'));
      return response.json;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 利用開始日を取得するメソッド
   */
  getInitialDate: (resultType: number, id: number) => {
    const clientId = getLocalStorageId();
    const url = `${API_URL}/${clientId}/reports/initial-date?resultType=${resultType}&id=${id}`;
    return httpClient(url).then(({ json }) => json);
  },

  checkExpireTerms: async () => {
    try {
      const url = `${API_URL}/me/check-expire-terms`;
      const response = await fetchUtils.fetchJson(url, getRequestOptions('GET'));
      return response.json;
    } catch (error) {
      console.error('Error in checkExpireTerms:', error);
      throw error;
    }
  },

  agreeTerms: async () => {
    try {
      const response = await dataProvider.register({}, '/clients/me/agree-terms');
      return response.json;
    } catch (error) {
      console.error('Error in agreeTerms:', error);
      throw error;
    }
  },

  agreeContract: async () => {
    try {
      const response = await dataProvider.register({}, '/clients/me/agree-contract');
      return response.json;
    } catch (error) {
      console.error('Error in agreeContract:', error);
      throw error;
    }
  },

  readNotify: async (id: string) => {
    try {
      const clientId = getLocalStorageId();
      const url = `${API_URL}/${clientId}/notifications/${id}/read`;
      const response = await dataProvider.register({}, url);
      return response.json;
    } catch (error) {
      console.error('Error in readNotify:', error);
      throw error;
    }
  },

  setRead: async (id: string) => {
    try {
      const clientId = getLocalStorageId();
      const url = `${API_URL}/${clientId}/affiliates/${id}/read`;
      const response = await dataProvider.register({}, url);
      return response.json;
    } catch (error) {
      console.error('Error in setRead:', error);
      throw error;
    }
  },

  download: async (url: string) => {
    const token = localStorage.getItem('token') ?? undefined;
    const response = await fetch(url, {
      method: 'GET',
      headers: new Headers({
        Accept: 'application/octet-stream',
        Authorization: `Bearer ${token}`
      })
    });

    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = 'untitled';
    if (contentDisposition && contentDisposition.includes('filename=')) {
      const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
      if (fileNameMatch && fileNameMatch.length === 2) {
        fileName = fileNameMatch[1];
      }
    }

    const blob = await response.blob();
    fileDownload(blob, fileName);
  },

  /**
   * PDFを新しいタブでプレビュー表示する
   * @param url PDFのエンドポイントURL
   */
  previewPdf: async (url: string) => {
    const token = localStorage.getItem('token') ?? undefined;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: new Headers({
          Accept: 'application/pdf', // PDFとして受け入れる
          Authorization: `Bearer ${token}`
        })
      });
      if (!response.ok) {
        throw new Error(`PDFの取得に失敗しました。ステータス: ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, '_blank');
      if (!newWindow) {
        throw new Error('ポップアップがブロックされました。ブラウザの設定を確認してください。');
      }
      // メモリリークを防ぐため、10秒後に生成した一時URLを無効化します。
      // (10秒あれば、新しいタブがPDFをロードするのに十分だろうという想定です)
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 10000);
    } catch (error) {
      if (error instanceof Error) {
        // Error オブジェクトなら、安全に .message を使える
        throw new Error(`PDFプレビューエラー: ${error.message}`);
      } else {
        // Error オブジェクト以外（文字列など）が投げられた場合のフォールバック
        throw new Error(`PDFプレビューエラー: 予期せぬエラーが発生しました (${String(error)})`);
      }
    }
  },

  //デフォルトはPOST
  register: async (data: Record<string, any>, url: string, method: string = 'POST') => {
    const dataWithBase64 = method === 'POST' ? await convertFilesInObjectToBase64(data) : data;
    const absoluteUrl = new URL(url, API_URL).toString();
    return httpClient(absoluteUrl, {
      method,
      body: method === 'POST' ? JSON.stringify(dataWithBase64) : undefined
    }).then(({ json }) => ({
      data: json
    }));
  },

  getOrderDetails: async (resource: string, params: { id: string }) => {
    const encodedId = encodeURIComponent(params.id);
    const clientId = getLocalStorageId();
    const url = `${API_URL}/${clientId}/${resource}/${encodedId}`;
    const { json } = await httpClient(url);
    return { data: json };
  },

  getManyDetails: async (resource: string, params: { ids: string[] }) => {
    const clientId = getLocalStorageId();
    const url = `${API_URL}/${clientId}/${resource}`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ ids: params.ids })
    });
    return { data: json };
  },

  testConnection: (params: { clientId: string }) => {
    const { clientId } = params;
    const url = `${API_URL}/${clientId}/api-settings/test-connection`;
    const options = getRequestOptions('POST');
    return fetchUtils.fetchJson(url, options);
  }
};

export default dataProvider;
